import { FunctionComponent, useState } from "react";
import { Box, Button, Typography, MenuItem, Checkbox, FormControlLabel } from '@material-ui/core'
import MembershipPatronPreview from '../../components/Memberships/MembershipPatronPreview'
import { TextField } from 'formik-material-ui'
import DetailOverlayPage from '../../components/DetailOverlay/Container'
import TopBar from '../../components/DetailOverlay/TopBar'
import { makeStyles } from '@material-ui/styles'
import { Formik, Field, Form, FieldArray } from 'formik'
import * as Yup from 'yup'
import useMembershipsStore from "../../stores/memberships";
import { useNavigate } from "react-router-dom";
import { MembershipCreateParams, BenefitParams, PricingParams, TaxRate } from "../../types";
import { toast } from "react-toastify";
import axios from '../../utils/axios'
import { useEffect } from 'react'
import { Stepper } from 'react-form-stepper'

const INTERVALS = [
  { value: 'once', label: 'Once (Lifetime)' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'semi_annual', label: 'Semi-Annual' },
  { value: 'annual', label: 'Annual' },
  { value: 'unlimited', label: 'Unlimited' },
]

const PRICING_INTERVALS = [
  { value: 'month', label: 'Monthly' },
  { value: 'year', label: 'Yearly' },
  { value: 'once', label: 'Lifetime' },
]

const STEPS = ['Basic Information', 'Benefits', 'Pricing', 'Preview']
const STEPPER_STEPS = STEPS.map(label => ({ label }))

const RESTRICTIONS = [
  { value: 'none', label: 'None' },
  { value: 'annual_only', label: 'Annual Only' },
  { value: 'monthly_only', label: 'Monthly Only' },
]

const NewMembership: FunctionComponent = () => {
  const [step, setStep] = useState<number>(1)
  const { createMembership } = useMembershipsStore()
  const navigate = useNavigate()
  const styles = useStyles()
  const [taxRates, setTaxRates] = useState<TaxRate[]>([])

  useEffect(() => {
    // Fetch tax rates
    const fetchTaxRates = async () => {
      try {
        const resp = await axios.get('/tax_rates')
        setTaxRates(resp.data)
      } catch (error) {
        console.error('Failed to fetch tax rates:', error)
      }
    }
    fetchTaxRates()
  }, [])

  const helpEmail = 'help@stitch.vip'
  const defaultErrorMessage = (
    <span>
      Failed to create membership. If this problem persists, please contact Stitch at &nbsp;
      <a href={`mailto:${helpEmail}`} className={styles.emailLink}>{helpEmail}</a>
    </span>
  )

  const initialValues: MembershipCreateParams = {
    membership: {
      title: '',
      description: '',
      fine_print: '',
      image_url: '',
      collect_address: false,
      collect_shirt_size: false,
      collect_birthdate: false,
      max_secondaries: 0,
      // Hardcoded: the type picker was removed from the form, so every
      // membership created here is standard. Kept in the payload shape
      // rather than deleted because the field is part of
      // MembershipCreateParams - and it is not sent anyway, see handleSubmit.
      membership_type: 'standard',
    },
    benefits: [],
    pricing: [],
  }

  const handleSubmit = async (formData: MembershipCreateParams) => {
    // Normalize numeric fields before sending to the API
    const payload: MembershipCreateParams = {
      ...formData,
      membership: {
        ...formData.membership,
        max_secondaries: 0,
        // TODO: Backend needs to implement membership_type column on memberships table
        // membership_type: formData.membership.membership_type,
      },
      benefits: formData.benefits?.map((benefit) => ({
        ...benefit,
        quantity: Number(benefit.quantity || 1),
        days_valid: benefit.days_valid !== undefined ? Number(benefit.days_valid) : undefined,
      })),
      pricing: formData.pricing?.map((price) => ({
        ...price,
        // Accept dollars in the UI, convert to cents for the API
        amount: Math.round(Number(price.amount || 0) * 100),
      })),
    }

    try {
      const membershipOrError = await createMembership(payload)
      if (typeof membershipOrError === 'string') {
        toast.error(`Error: ${membershipOrError}` || defaultErrorMessage)
        return
      }

      const membership = membershipOrError

      navigate(`/memberships/${membership.uuid}`);
      toast.success(`Added ${formData.membership.title}`)
    } catch (error) {
      toast.error(defaultErrorMessage)
    }
  }

  const validationSchema = Yup.object({
    membership: Yup.object({
      title: Yup.string().required('Title is required'),
      description: Yup.string(),
      fine_print: Yup.string(),
      image_url: Yup.string().url('Must be a valid URL'),
      max_secondaries: Yup.number().min(0),
    }),
    benefits: Yup.array()
      .of(
        Yup.object({
          title: Yup.string().required('Benefit title is required'),
          interval: Yup.string().required('Interval is required'),
          quantity: Yup.number().min(1).required('Quantity is required'),
        })
      )
      .min(1, 'At least one benefit is required'),
    pricing: Yup.array()
      .of(
        Yup.object({
          amount: Yup.number().min(0).required('Amount is required'),
          interval: Yup.string().required('Interval is required'),
        })
      )
      .min(1, 'Must include 1 or 2 pricing options')
      .max(2, 'Must include 1 or 2 pricing options'),
  })

  return (
    <DetailOverlayPage>
      <TopBar breadCrumbs={[
        { title: 'Memberships', linkPath: '/memberships' },
        { title: 'New Membership', linkPath: '' },
      ]} closePath={'/memberships'}
      />
      <Box p={4} mb={4} className={styles.container}>
        <Box className={styles.formContainer}>
          <Box className={styles.titleContainer} mb={2}>
            <Typography variant="h3">Membership Creation</Typography>
          </Box>
          <Box mb={3}>
            <Stepper
              steps={STEPPER_STEPS}
              activeStep={step - 1}
              connectorStateColors
              styleConfig={{
                activeBgColor: '#3f51b5',
                activeTextColor: '#fff',
                completedBgColor: '#3f51b5',
                completedTextColor: '#fff',
                inactiveBgColor: '#e0e0e0',
                inactiveTextColor: '#757575',
                size: '32px',
                circleFontSize: '14px',
                labelFontSize: '14px',
                borderRadius: '50%',
                fontWeight: 500,
              }}
            />
          </Box>

          <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
          >
            {({ values, errors, touched, setFieldValue, validateForm, setTouched }) => {
              const getStepFieldPaths = (currentStep: number) => {
                if (currentStep === 1) {
                  return [
                    'membership.title',
                    'membership.description',
                    'membership.fine_print',
                    'membership.image_url',
                  ]
                }
                if (currentStep === 2) {
                  return (values.benefits || []).flatMap((_, index) => ([
                    `benefits.${index}.title`,
                    `benefits.${index}.interval`,
                    `benefits.${index}.quantity`,
                    `benefits.${index}.details`,
                    `benefits.${index}.fine_print`,
                    `benefits.${index}.days_valid`,
                    `benefits.${index}.restriction`,
                  ]))
                }
                if (currentStep === 3) {
                  return (values.pricing || []).flatMap((_, index) => ([
                    `pricing.${index}.amount`,
                    `pricing.${index}.interval`,
                    `pricing.${index}.nickname`,
                    `pricing.${index}.tax_rate_ids`,
                  ]))
                }
                return []
              }

              const getIn = (obj: any, path: string) => {
                return path.split('.').reduce((acc, key) => (acc ? acc[key] : undefined), obj)
              }

              const handleNext = async () => {
                const stepFields = getStepFieldPaths(step)
                const validationErrors = await validateForm()

                // Check for entity-level errors (benefits/pricing arrays)
                let hasStepErrors = stepFields.some((fieldPath) => !!getIn(validationErrors, fieldPath))

                if (step === 2 && validationErrors.benefits && typeof validationErrors.benefits === 'string') {
                  hasStepErrors = true
                }
                if (step === 3 && validationErrors.pricing && typeof validationErrors.pricing === 'string') {
                  hasStepErrors = true
                }

                if (hasStepErrors) {
                  // Mark current step fields as touched to surface validation messages
                  const touchedFields = stepFields.reduce((acc: any, path) => {
                    const keys = path.split('.')
                    let cursor = acc
                    keys.forEach((key, idx) => {
                      if (!cursor[key]) {
                        cursor[key] = {}
                      }
                      if (idx === keys.length - 1) {
                        cursor[key] = true
                      } else {
                        cursor = cursor[key]
                      }
                    })
                    return acc
                  }, {})

                  // Also set touched state for benefits/pricing arrays to show entity errors
                  if (step === 2) {
                    setTouched({ ...touched, ...touchedFields, benefits: true }, false)
                  } else if (step === 3) {
                    setTouched({ ...touched, ...touchedFields, pricing: true }, false)
                  } else {
                    setTouched({ ...touched, ...touchedFields }, false)
                  }
                  return
                }

                setStep(step + 1)
              }

              return (
              <Box className={styles.splitLayout}>
              <Form className={styles.formColumn}>
                {step === 1 && (
                  <Box>
                    <Typography variant="h5" gutterBottom>Basic Information</Typography>
                    <Box mb={2}>
                      <Field
                        fullWidth
                        required
                        component={TextField}
                        name="membership.title"
                        label="Membership Title"
                        error={touched.membership?.title && Boolean(errors.membership?.title)}
                        helperText={touched.membership?.title && errors.membership?.title ? errors.membership?.title : "The name members will see for this membership (e.g., 'Gold Member', 'VIP Access')"}
                      />
                    </Box>
                    <Box mb={2}>
                      <Field
                        fullWidth
                        multiline
                        rows={4}
                        component={TextField}
                        name="membership.description"
                        label="Description"
                        helperText="A detailed description of what this membership includes and its value to members"
                      />
                    </Box>
                    <Box mb={2}>
                      <Field
                        fullWidth
                        multiline
                        rows={2}
                        component={TextField}
                        name="membership.fine_print"
                        label="Fine Print"
                        helperText="Terms, conditions, or important disclaimers members should know (e.g., cancellation policy, restrictions)"
                      />
                    </Box>
                    <Box mb={2}>
                      {/* TODO: Replace manual URL input with file upload component that automatically uploads to S3 and saves the URL to the database */}
                      <Field
                        fullWidth
                        component={TextField}
                        name="membership.image_url"
                        label="Image URL"
                        error={touched.membership?.image_url && Boolean(errors.membership?.image_url)}
                        helperText={touched.membership?.image_url && errors.membership?.image_url ? errors.membership?.image_url : "URL to the membership card or badge image. Leave blank to use organization's default logo"}
                      />
                    </Box>
                    <Box mb={2}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={values.membership.collect_address}
                            onChange={(e) => setFieldValue('membership.collect_address', e.target.checked)}
                          />
                        }
                        label="Collect Address"
                      />
                      <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: -8, marginLeft: 32 }}>
                        Request member's mailing address during signup (useful for shipping physical items)
                      </Typography>
                    </Box>
                    <Box mb={2}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={values.membership.collect_shirt_size}
                            onChange={(e) => setFieldValue('membership.collect_shirt_size', e.target.checked)}
                          />
                        }
                        label="Collect Shirt Size"
                      />
                      <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: -8, marginLeft: 32 }}>
                        Request member's shirt size during signup (useful if membership includes apparel)
                      </Typography>
                    </Box>
                    <Box mb={2}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={values.membership.collect_birthdate}
                            onChange={(e) => setFieldValue('membership.collect_birthdate', e.target.checked)}
                          />
                        }
                        label="Collect Birthdate"
                      />
                      <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: -8, marginLeft: 32 }}>
                        Request member's birthdate during signup (useful for birthday promotions or age verification)
                      </Typography>
                    </Box>
                  </Box>
                )}

                {step === 2 && (
                  <Box>
                    <Typography variant="h5" gutterBottom>Benefits</Typography>
                    {touched.benefits && errors.benefits && typeof errors.benefits === 'string' && (
                      <Typography variant="body2" color="error" gutterBottom>
                        {errors.benefits}
                      </Typography>
                    )}
                    <FieldArray name="benefits">
                      {({ push, remove }) => (
                        <Box>
                          {values.benefits && values.benefits.length > 0 ? (
                            values.benefits.map((benefit: BenefitParams, index: number) => (
                              <Box key={index} mb={3} p={2} style={{ border: '1px solid #e0e0e0', borderRadius: 4 }}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                  <Typography variant="h6">Benefit {index + 1}</Typography>
                                  <Button onClick={() => remove(index)} size="small" color="secondary">
                                    Remove
                                  </Button>
                                </Box>
                                <Box mb={2}>
                                  <Field
                                    fullWidth
                                    required
                                    component={TextField}
                                    name={`benefits.${index}.title`}
                                    label="Title"
                                    helperText="Name of the benefit (e.g., 'Free Coffee', 'Event Tickets', 'Exclusive Merchandise')"
                                  />
                                </Box>
                                <Box mb={2}>
                                  <Field
                                    fullWidth
                                    multiline
                                    rows={2}
                                    component={TextField}
                                    name={`benefits.${index}.details`}
                                    label="Details"
                                    helperText="Detailed description of the benefit, what it includes, and how members can use it"
                                  />
                                </Box>
                                <Box mb={2}>
                                  <Field
                                    fullWidth
                                    component={TextField}
                                    name={`benefits.${index}.fine_print`}
                                    label="Fine Print"
                                    helperText="Important restrictions or conditions for this benefit (e.g., 'Valid Monday-Thursday only')"
                                  />
                                </Box>
                                <Box mb={2}>
                                  <Field
                                    fullWidth
                                    select
                                    required
                                    component={TextField}
                                    name={`benefits.${index}.interval`}
                                    label="Interval"
                                    helperText="How often members receive this benefit (e.g., Monthly = each billing cycle, Once = one-time only)"
                                  >
                                    {INTERVALS.map((option) => (
                                      <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                      </MenuItem>
                                    ))}
                                  </Field>
                                </Box>
                                <Box mb={2}>
                                  <Field
                                    fullWidth
                                    required
                                    component={TextField}
                                    name={`benefits.${index}.quantity`}
                                    label="Quantity"
                                    type="number"
                                    inputProps={{ min: 1 }}
                                    helperText="Number of times the member can use this benefit per interval (e.g., 2 = two free coffees per month)"
                                  />
                                </Box>
                                <Box mb={2}>
                                  <Field
                                    fullWidth
                                    component={TextField}
                                    name={`benefits.${index}.days_valid`}
                                    label="Days Valid (default: 30)"
                                    type="number"
                                    helperText="Number of days this benefit remains valid after being issued to a member"
                                  />
                                </Box>
                                <Box mb={2}>
                                  <Field
                                    fullWidth
                                    select
                                    component={TextField}
                                    name={`benefits.${index}.restriction`}
                                    label="Restriction"
                                    helperText="Limit this benefit to specific subscription types (Annual Only, Monthly Only, or None for all)"
                                  >
                                    {RESTRICTIONS.map((option) => (
                                      <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                      </MenuItem>
                                    ))}
                                  </Field>
                                </Box>
                              </Box>
                            ))
                          ) : (
                            <Typography variant="body2" color="textSecondary">No benefits added yet</Typography>
                          )}
                          <Button
                            variant="outlined"
                            onClick={() => push({
                              title: '',
                              details: '',
                              fine_print: '',
                              interval: 'monthly',
                              quantity: 1,
                              days_valid: 30,
                              restriction: 'none',
                            })}
                          >
                            + Add Benefit
                          </Button>
                        </Box>
                      )}
                    </FieldArray>
                  </Box>
                )}

                {step === 3 && (
                  <Box>
                    <Typography variant="h5" gutterBottom>Pricing</Typography>
                    {touched.pricing && errors.pricing && typeof errors.pricing === 'string' && (
                      <Typography variant="body2" color="error" gutterBottom>
                        {errors.pricing}
                      </Typography>
                    )}
                    <FieldArray name="pricing">
                      {({ push, remove }) => (
                        <Box>
                          {values.pricing && values.pricing.length > 0 ? (
                            values.pricing.map((price: PricingParams, index: number) => (
                              <Box key={index} mb={3} p={2} style={{ border: '1px solid #e0e0e0', borderRadius: 4 }}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                  <Typography variant="h6">Price {index + 1}</Typography>
                                  <Button onClick={() => remove(index)} size="small" color="secondary">
                                    Remove
                                  </Button>
                                </Box>
                                <Box mb={2}>
                                  <Field
                                    fullWidth
                                    required
                                    component={TextField}
                                    name={`pricing.${index}.amount`}
                                    label="Amount (USD)"
                                    type="number"
                                    inputProps={{ step: 0.01, min: 0 }}
                                    helperText="Price in dollars (e.g., 9.99 for $9.99/month or 99.00 for $99/year)"
                                  />
                                </Box>
                                <Box mb={2}>
                                  <Field
                                    fullWidth
                                    select
                                    required
                                    component={TextField}
                                    name={`pricing.${index}.interval`}
                                    label="Interval"
                                    helperText="Billing frequency - how often members are charged for this subscription"
                                  >
                                    {PRICING_INTERVALS.map((option) => (
                                      <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                      </MenuItem>
                                    ))}
                                  </Field>
                                </Box>
                                <Box mb={2}>
                                  <Field
                                    fullWidth
                                    component={TextField}
                                    name={`pricing.${index}.nickname`}
                                    label="Nickname"
                                    helperText="Optional display name for this price tier (e.g., 'Standard Plan', 'Best Value')"
                                  />
                                </Box>
                                {taxRates.length > 0 && (
                                  <Box mb={2}>
                                    <Field
                                      fullWidth
                                      select
                                      component={TextField}
                                      name={`pricing.${index}.tax_rate_ids`}
                                      label="Tax Rates (Optional)"
                                      helperText="Select applicable tax rates for this price. Multiple rates can be applied if needed"
                                      SelectProps={{
                                        multiple: true,
                                      }}
                                    >
                                      {taxRates.map((taxRate) => (
                                        <MenuItem key={taxRate.id} value={taxRate.id}>
                                          {taxRate.display_name} ({taxRate.rate}%)
                                        </MenuItem>
                                      ))}
                                    </Field>
                                  </Box>
                                )}
                              </Box>
                            ))
                          ) : (
                            <Typography variant="body2" color="textSecondary">No pricing added yet</Typography>
                          )}
                          <Button
                            variant="outlined"
                            disabled={(values.pricing?.length || 0) >= 2}
                            onClick={() => push({
                              amount: 0,
                              interval: 'month',
                              nickname: '',
                              tax_rate_ids: [],
                            })}
                          >
                            + Add Price
                          </Button>
                          {(values.pricing?.length || 0) >= 2 && (
                            <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 8 }}>
                              You can add up to two pricing options.
                            </Typography>
                          )}
                        </Box>
                      )}
                    </FieldArray>
                  </Box>
                )}

                {step === 4 && (
                  <Box>
                    <Typography variant="h5" gutterBottom>Preview</Typography>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Review your membership details before creating
                    </Typography>

                    <Box mt={3} mb={3} p={2} style={{ border: '1px solid #e0e0e0', borderRadius: 4, backgroundColor: '#f9f9f9' }}>
                      <Typography variant="h6" gutterBottom>Basic Information</Typography>
                      <Box mb={2}>
                        <Typography variant="body2" color="textSecondary">Title</Typography>
                        <Typography variant="body1">{values.membership.title || 'N/A'}</Typography>
                      </Box>
                      {values.membership.description && (
                        <Box mb={2}>
                          <Typography variant="body2" color="textSecondary">Description</Typography>
                          <Typography variant="body1">{values.membership.description}</Typography>
                        </Box>
                      )}
                      {values.membership.fine_print && (
                        <Box mb={2}>
                          <Typography variant="body2" color="textSecondary">Fine Print</Typography>
                          <Typography variant="body1">{values.membership.fine_print}</Typography>
                        </Box>
                      )}
                      {values.membership.image_url && (
                        <Box mb={2}>
                          <Typography variant="body2" color="textSecondary">Image URL</Typography>
                          <Typography variant="body1">{values.membership.image_url}</Typography>
                        </Box>
                      )}
                      <Box mb={2}>
                        <Typography variant="body2" color="textSecondary">Collection Options</Typography>
                        <Typography variant="body1">
                          {[
                            values.membership.collect_address && 'Address',
                            values.membership.collect_shirt_size && 'Shirt Size',
                            values.membership.collect_birthdate && 'Birthdate',
                          ].filter(Boolean).join(', ') || 'None'}
                        </Typography>
                      </Box>
                    </Box>

                    <Box mb={3} p={2} style={{ border: '1px solid #e0e0e0', borderRadius: 4, backgroundColor: '#f9f9f9' }}>
                      <Typography variant="h6" gutterBottom>Benefits ({values.benefits?.length || 0})</Typography>
                      {values.benefits && values.benefits.length > 0 ? (
                        values.benefits.map((benefit: BenefitParams, index: number) => (
                          <Box key={index} mb={2} p={1.5} style={{ border: '1px solid #ddd', borderRadius: 4, backgroundColor: '#fff' }}>
                            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>{benefit.title}</Typography>
                            {benefit.details && <Typography variant="body2">{benefit.details}</Typography>}
                            <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                              Interval: {INTERVALS.find(i => i.value === benefit.interval)?.label || benefit.interval} |
                              Quantity: {benefit.quantity} |
                              Days Valid: {benefit.days_valid || 30}
                              {benefit.restriction && benefit.restriction !== 'none' && ` | Restriction: ${RESTRICTIONS.find(r => r.value === benefit.restriction)?.label}`}
                            </Typography>
                            {benefit.fine_print && (
                              <Typography variant="caption" color="textSecondary" style={{ marginTop: 4, display: 'block' }}>
                                Fine Print: {benefit.fine_print}
                              </Typography>
                            )}
                          </Box>
                        ))
                      ) : (
                        <Typography variant="body2" color="textSecondary">No benefits added</Typography>
                      )}
                    </Box>

                    <Box mb={3} p={2} style={{ border: '1px solid #e0e0e0', borderRadius: 4, backgroundColor: '#f9f9f9' }}>
                      <Typography variant="h6" gutterBottom>Pricing ({values.pricing?.length || 0})</Typography>
                      {values.pricing && values.pricing.length > 0 ? (
                        values.pricing.map((price: PricingParams, index: number) => (
                          <Box key={index} mb={2} p={1.5} style={{ border: '1px solid #ddd', borderRadius: 4, backgroundColor: '#fff' }}>
                            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                              ${Number(price.amount).toFixed(2)} / {PRICING_INTERVALS.find(i => i.value === price.interval)?.label}
                            </Typography>
                            {price.nickname && (
                              <Typography variant="body2" color="textSecondary">Nickname: {price.nickname}</Typography>
                            )}
                            {price.tax_rate_ids && price.tax_rate_ids.length > 0 && (
                              <Typography variant="body2" color="textSecondary">
                                Tax Rates: {price.tax_rate_ids.map(id => {
                                  const rate = taxRates.find(r => r.id === id)
                                  return rate ? `${rate.display_name} (${rate.rate}%)` : id
                                }).join(', ')}
                              </Typography>
                            )}
                          </Box>
                        ))
                      ) : (
                        <Typography variant="body2" color="textSecondary">No pricing added</Typography>
                      )}
                    </Box>
                  </Box>
                )}

                <Box mt={3} className={styles.buttonContainer}>
                  {step > 1 && (
                    <Box mr={2}>
                      <Button
                        type="button"
                        variant="outlined"
                        onClick={() => setStep(step - 1)}
                      >
                        Previous
                      </Button>
                    </Box>
                  )}
                  {step < 4 ? (
                    <Button
                      type="button"
                      variant="contained"
                      color="primary"
                      onClick={handleNext}
                    >
                      Next
                    </Button>
                  ) : (
                    <Box display="flex">
                      <Box mr={2}>
                        <Button
                          type="button"
                          variant="text"
                          color="secondary"
                          onClick={() => navigate('/memberships')}
                        >
                          Cancel
                        </Button>
                      </Box>
                      <Button type="submit" variant="contained" color="primary">
                        Create Membership
                      </Button>
                    </Box>
                  )}
                </Box>
              </Form>

              {/* Sticky, and live off Formik's values, so a brewery sees its
                  own words land in the patron's view as it writes them. */}
              <Box className={styles.previewColumn}>
                <MembershipPatronPreview
                  title={values.membership.title}
                  description={values.membership.description}
                  finePrint={values.membership.fine_print}
                  imageUrl={values.membership.image_url}
                  benefits={values.benefits}
                  pricing={values.pricing}
                />
              </Box>
              </Box>
              )
            }}
          </Formik>
        </Box>
      </Box>
    </DetailOverlayPage>
  )
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    justifyContent: 'center',
    overflowY: 'auto',
    flex: 1,
  },
  formContainer: {
    width: '90%',
    maxWidth: 1180,
  },
  splitLayout: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 32,
  },
  formColumn: {
    flex: 1,
    minWidth: 0,
  },
  previewColumn: {
    width: 300,
    flexShrink: 0,
    // The form is the job; below this the preview would squeeze it.
    '@media (max-width: 1100px)': {
      display: 'none',
    },
  },
  cancelButton: {
    display: 'inline'
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  titleContainer: {
    height: 30,
    display: 'flex',
    justifyContent: 'space-between',
  },
  emailLink: {
    textDecoration: 'underline'
  }
})

export default NewMembership
